/**
 * API Client Module
 *
 * Provides REST API communication helpers for the MonetaApp.
 * Includes methods for GET, POST, PATCH, DELETE with automatic
 * header injection and fallback to direct fetch when needed.
 */

import { get, post, del, patch } from 'aws-amplify/api';

import awsconfig from '../../src/aws-exports';
import { getToken, addHeaders } from '../auth/tokenManager';
import logger from '../logger/logger';

/**
 * In-flight request cache for deduplication.
 * Maps request keys to their pending promises.
 */
const inFlightRequests = new Map();

/**
 * Generate a cache key for request deduplication
 * @param {string} apiName - The API name
 * @param {string} path - The API path
 * @param {Object} options - Request options
 * @returns {string} Cache key
 */
function generateRequestKey(apiName, path, options = {}) {
  const queryParams = options.queryParams || {};
  const sortedParams = Object.keys(queryParams)
    .sort()
    .map((k) => `${k}=${queryParams[k]}`)
    .join('&');
  return `${apiName}:${path}?${sortedParams}`;
}

/**
 * Clear all in-flight requests (useful on logout or app reset)
 */
export function clearInFlightRequests() {
  inFlightRequests.clear();
}

/**
 * Get the endpoint URL for a named API
 * @param {string} apiName - The API name from aws-exports
 * @returns {string} The endpoint URL
 */
export function endpointFor(apiName) {
  const hit = (awsconfig.aws_cloud_logic_custom ?? []).find((e) => e.name === apiName);
  if (!hit) throw new Error(`No endpoint for API "${apiName}"`);
  return hit.endpoint;
}

/**
 * Direct fetch fallback when Amplify API is not configured
 * @param {string} apiName - The API name
 * @param {string} path - The API path
 * @param {Object} options - Request options (queryParams, headers, body)
 * @param {string} method - HTTP method
 * @returns {Promise<Object>} Response JSON
 */
export async function directFetch(
  apiName,
  path,
  { queryParams, headers = {}, body } = {},
  method = 'GET',
) {
  console.log('[REST-FALLBACK]', apiName, path, method);

  const base = endpointFor(apiName);

  const qp = new URLSearchParams();
  if (queryParams) {
    Object.entries(queryParams).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach((x) => qp.append(k, String(x)));
      else if (v != null) qp.append(k, String(v));
    });
  }

  // Prefer existing header; only add if missing
  const finalHeaders = { ...headers };
  if (!finalHeaders.Authorization && !finalHeaders.authorization) {
    const token = (await getToken()) || '';
    if (token) finalHeaders.Authorization = token;
  }

  const hasBody = body !== undefined && body !== null;
  if (hasBody && !(body instanceof FormData) && !finalHeaders['Content-Type']) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  const res = await fetch(`${base}${path}${qp.toString() ? `?${qp}` : ''}`, {
    method,
    headers: finalHeaders,
    body: hasBody ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
  });

  if (!res.ok) throw new Error(`${res.status} ${await res.text()}`);
  return res.json();
}

/**
 * Make a GET request to the API with request deduplication.
 * If an identical request is already in-flight, returns the same promise.
 * @param {string} apiName - The API name
 * @param {string} path - The API path
 * @param {Object} options - Request options
 * @param {boolean} noLogin - Skip auth headers
 * @returns {Promise<Object>} Response data
 */
export async function apiGet(apiName, path, options, noLogin = false) {
  if (!options) {
    options = {};
  }

  // Generate key for deduplication (before adding auth headers)
  const requestKey = generateRequestKey(apiName, path, options);

  // Check if there's already an in-flight request for this key
  if (inFlightRequests.has(requestKey)) {
    return inFlightRequests.get(requestKey);
  }

  // Create the request promise
  const requestPromise = (async () => {
    let optionsWithHeaders = options;
    if (!noLogin) {
      optionsWithHeaders = await addHeaders(options);
    }

    try {
      const { body } = await get({ apiName: apiName, path: path, options: optionsWithHeaders })
        .response;
      return await body.json();
    } catch (e) {
      if (String(e).includes('API name is invalid')) {
        return directFetch(apiName, path, optionsWithHeaders, 'GET');
      }
      throw e;
    }
  })();

  // Store the promise and clean up when done
  inFlightRequests.set(requestKey, requestPromise);
  requestPromise
    .finally(() => {
      inFlightRequests.delete(requestKey);
    })
    .catch(() => {
      // Swallow error - it will be handled by the caller awaiting requestPromise
    });

  return requestPromise;
}

/**
 * Extract error details from Amplify v6 API errors
 * @param {Error} e - The error from Amplify API
 * @returns {Promise<Error>} Enhanced error with parsed response body
 */
async function enhanceApiError(e) {
  // Amplify v6 error structure:
  // - e.response.statusCode, e.response.headers, e.response.body
  // - e.response.body is typically a string containing JSON

  // Approach 1: Parse response.body as JSON string (Amplify v6 structure)
  if (e.response?.body) {
    try {
      const body = typeof e.response.body === 'string'
        ? JSON.parse(e.response.body)
        : e.response.body;

      if (body?.error) {
        const enhancedError = new Error(body.error);
        enhancedError.error = body.error;
        enhancedError.status = e.response.statusCode || e.response.status;
        enhancedError.originalError = e;
        return enhancedError;
      }
    } catch {
      // Failed to parse body
    }
  }

  // Approach 2: Check if response.body.json is a function (ReadableStream)
  if (e.response && typeof e.response.body?.json === 'function') {
    try {
      const errorBody = await e.response.body.json();
      if (errorBody?.error) {
        const enhancedError = new Error(errorBody.error);
        enhancedError.error = errorBody.error;
        enhancedError.status = e.response.statusCode || e.response.status;
        enhancedError.originalError = e;
        return enhancedError;
      }
    } catch {
      // Failed to parse body.json()
    }
  }

  // Approach 3: Check direct error properties
  const possibleError = e.error || e.data?.error || e.body?.error;
  if (possibleError && typeof possibleError === 'string') {
    const enhancedError = new Error(possibleError);
    enhancedError.error = possibleError;
    return enhancedError;
  }

  return e;
}

/**
 * Make a POST request to the API
 * @param {string} apiName - The API name
 * @param {string} path - The API path
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Response data
 */
export async function apiPost(apiName, path, options) {
  options = await addHeaders(options);

  try {
    const { body } = await post({ apiName: apiName, path: path, options: options }).response;
    return await body.json();
  } catch (e) {
    if (String(e).includes('API name is invalid')) {
      return directFetch(apiName, path, options, 'POST');
    }
    throw await enhanceApiError(e);
  }
}

/**
 * Make a PATCH request to the API
 * @param {string} apiName - The API name
 * @param {string} path - The API path
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Response data
 */
export async function apiPatch(apiName, path, options) {
  options = await addHeaders(options);

  try {
    const { body } = await patch({ apiName: apiName, path: path, options: options }).response;
    return await body.json();
  } catch (e) {
    if (String(e).includes('API name is invalid')) {
      return directFetch(apiName, path, options, 'PATCH');
    }
    throw e;
  }
}

/**
 * Make a DELETE request to the API
 * @param {string} apiName - The API name
 * @param {string} path - The API path
 * @param {Object} options - Request options
 * @returns {Promise<Object>} Response data
 */
export async function apiDel(apiName, path, options) {
  options = await addHeaders(options);

  try {
    const { body } = await del({ apiName: apiName, path: path, options: options }).response;
    return await body.json();
  } catch (e) {
    if (String(e).includes('API name is invalid')) {
      return directFetch(apiName, path, options, 'DELETE');
    }
    throw e;
  }
}

/**
 * Print API error details for debugging
 * @param {Error} error - The error object
 */
export function printAPIError(error) {
  const apiLogger = logger.module('API');

  if (error.response) {
    apiLogger.error('API request failed', {
      data: error.response.data,
      status: error.response.status,
      headers: error.response.headers,
      config: error.config,
    });
  } else if (error.request) {
    apiLogger.error('API request failed (no response)', {
      request: error.request,
      config: error.config,
    });
  } else {
    apiLogger.error('API error', {
      message: error.message,
      config: error.config,
    });
  }
}
